import { useCallback, useContext } from 'react';
import { useSelectedLinkContext } from 'contexts';
import { shareContext } from '../../ShareContext';
import { useMutation } from '@apollo/client';
import {
  DELETE_FOLDER_MUTATION,
  DeleteFolderOutput,
  SELF_UNSUBSCRIBE_MUTATION,
  SelfUnsubscribeMutationOutput,
} from 'graphql/folder';
import { settingsContext } from './SettingsContext';
import {
  getHandleValidateFunction,
  getModalMessagesId,
  getUpdateCacheFunction,
} from './DeleteOrQuitFolderHelpers';
import { Grid, Paper } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { FilledButton } from 'components';

import useStyles from '../Modals.styles';
import useDeleteOrQuitFolderModalStyles from './DeleteOrQuitFolderConfirmationModal.styles';
import {useSnackbar} from 'hooks';

type DeleteOrQuitFolderConfirmationModalPureProps = {
  descriptionMessageId: string;
  titleMessageId: string;
  handleHide: () => void;
  handleValidate: () => void;
};

export const DeleteOrQuitFolderConfirmationModalPure = ({
  descriptionMessageId,
  titleMessageId,
  handleHide,
  handleValidate,
}: DeleteOrQuitFolderConfirmationModalPureProps): JSX.Element => {
  const classes = useStyles();
  const deleteOrQuitFolderModalStyles = useDeleteOrQuitFolderModalStyles();

  return (
    <div className={classes.container}>
      <Paper className={deleteOrQuitFolderModalStyles.modal}>
        <FilledButton
          className={deleteOrQuitFolderModalStyles.cancelButton}
          color='red'
          data-testid="cancel-button"
          onClick={handleHide}
        >
          <FormattedMessage id="button.cancel" />
        </FilledButton>

        <Grid className={deleteOrQuitFolderModalStyles.content} container>
          <Grid className={classes.text} item xs={12}>
            <FormattedMessage id={titleMessageId} />
          </Grid>

          <Grid
            className={deleteOrQuitFolderModalStyles.description}
            item
            xs={12}
          >
            <FormattedMessage id={descriptionMessageId} />
          </Grid>

          <Grid
            className={deleteOrQuitFolderModalStyles.validateButtonContainer}
            item
            xs={12}
          >
            <FilledButton
              className={deleteOrQuitFolderModalStyles.validateButton}
              color="violet"
              data-testid="delete-or-quit-folder-confirm-button"
              onClick={handleValidate}
            >
              <FormattedMessage id={titleMessageId} />
            </FilledButton>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

const DeleteOrQuitFolderConfirmationModal = (): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const { selectedFileType, selectedFolder, setSelectedFolder } =
    useContext(shareContext);
  const {successSnackbar, errorSnackbar} = useSnackbar();

  const { showDeleteOrQuitModal, setShowDeleteOrQuitModal } =
    useContext(settingsContext);

  const isFolderAdmin = selectedFolder?.creator === selectedLink?.userKey;

  const updateCache = getUpdateCacheFunction({
    selectedFileType,
    selectedFolder,
    selectedLink,
    setSelectedFolder,
    setShowDeleteOrQuitModal,
  });

  const [deleteFolder] = useMutation<DeleteFolderOutput>(
    DELETE_FOLDER_MUTATION,
    {
      update(cache, { data }) {
        const isDeleted = data?.deleteFolder.isDeleted;

        if (isDeleted) {
          updateCache(cache);
        }
      },
    },
  );

  const [selfUnsubscribe] = useMutation<SelfUnsubscribeMutationOutput>(
    SELF_UNSUBSCRIBE_MUTATION,
    {
      update(cache, { data }) {
        const folder = data?.selfUnsubscribe.folder;

        if (folder) {
          updateCache(cache);
        }
      },
    },
  );

  const handleValidateFunction = getHandleValidateFunction({
    isFolderAdmin,
    selectedLink,
    selectedFolder,
    deleteFolder,
    selfUnsubscribe,
    errorSnackbar,
    successSnackbar,
  });

  const handleValidate = useCallback(handleValidateFunction, [
    isFolderAdmin,
    selectedLink,
    selectedFolder,
    deleteFolder,
    handleValidateFunction,
    selfUnsubscribe,
    errorSnackbar,
    successSnackbar,
  ]);

  const handleHide = useCallback(
    () => setShowDeleteOrQuitModal(false),
    [setShowDeleteOrQuitModal],
  );

  if (!showDeleteOrQuitModal) {
    return null;
  }

  const { descriptionMessageId, titleMessageId } = getModalMessagesId({
    isFolderAdmin,
  });

  return (
    <DeleteOrQuitFolderConfirmationModalPure
      descriptionMessageId={descriptionMessageId}
      titleMessageId={titleMessageId}
      handleHide={handleHide}
      handleValidate={handleValidate}
    />
  );
};

export default DeleteOrQuitFolderConfirmationModal;
